import type { JSX, ReactNode } from 'react';
import { Button } from './Button';

export interface ToggleButtonProps {
  /**
   * Whether the toggle is checked/active
   */
  checked: boolean;

  /**
   * Callback when toggle state changes
   */
  onChange: (value: boolean) => void;

  /**
   * Button label text
   */
  label: string;

  /**
   * Show checkmark icon when checked
   * @default true
   */
  showCheckmark?: boolean;

  /**
   * Optional description shown when NOT checked
   */
  description?: ReactNode;

  /**
   * Additional className for the container
   */
  className?: string;
}

/**
 * ToggleButton component - button that toggles between checked/unchecked states
 *
 * Features:
 * - Visual state with primary/outline variants
 * - Optional checkmark icon when checked
 * - Optional description text shown when unchecked
 * - Full-width by default
 *
 * @example
 * ```tsx
 * <ToggleButton
 *   checked={includePartner}
 *   onChange={setIncludePartner}
 *   label="Include Partner"
 *   description="Partner's income affects child benefit calculations"
 * />
 * ```
 */
export function ToggleButton({
  checked,
  onChange,
  label,
  showCheckmark = true,
  description,
  className = '',
}: ToggleButtonProps): JSX.Element {
  return (
    <section className={className}>
      <Button
        onClick={() => onChange(!checked)}
        variant={checked ? 'primary' : 'outline'}
        fullWidth
      >
        {checked && showCheckmark ? '✓ ' : ''}
        {label}
      </Button>
      {description && !checked && (
        <div className="mt-2">{description}</div>
      )}
    </section>
  );
}
